"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Download, Loader2, CheckCircle2, AlertCircle } from "@/lib/icons"
import { useSettingsStore } from "@/lib/settings-store"
import { useBatchDownloadStore } from "@/lib/batch-download-store"
import type { BooruPost } from "@/lib/booru-api"
import { useTranslations } from "next-intl"

function getFileExtensionFromUrl(url: string): string {
  const sanitized = url.split("#")[0]?.split("?")[0] ?? url
  const lastSlash = sanitized.lastIndexOf("/")
  const lastDot = sanitized.lastIndexOf(".")
  if (lastDot > lastSlash) return sanitized.slice(lastDot + 1).toLowerCase() || "bin"
  return "bin"
}

interface BatchDownloadModalProps {
  isOpen: boolean
  onClose: () => void
  posts: BooruPost[]
  source: "favorites" | "tags" | "collections"
  sourceName?: string
}

export function BatchDownloadModal({ isOpen, onClose, posts, source, sourceName }: BatchDownloadModalProps) {
  const t = useTranslations("batchDownloadModal")
  const { saveLocation, customSavePath, setSaveLocation, setCustomSavePath, createSubfolder, setCreateSubfolder } =
    useSettingsStore()
  const {
    isDownloading,
    progress,
    currentFile,
    totalFiles,
    downloadedFiles,
    setIsDownloading,
    setProgress,
    setCurrentFile,
    setTotalFiles,
    setDownloadedFiles,
    reset,
  } = useBatchDownloadStore()

  const [downloadFormat, setDownloadFormat] = useState<"zip" | "individual">("individual")
  const [subfolder, setSubfolder] = useState(sourceName || source)
  const [status, setStatus] = useState<"idle" | "downloading" | "complete" | "error">("idle")

  const handleDownload = async () => {
    if (posts.length === 0) return

    setIsDownloading(true)
    setStatus("downloading")
    setTotalFiles(posts.length)
    setDownloadedFiles(0)
    setProgress(0)

    try {
      if (downloadFormat === "zip") {
        await downloadAsZip()
      } else {
        await downloadIndividual()
      }
      setStatus("complete")
      setTimeout(() => {
        onClose()
        reset()
        setStatus("idle")
      }, 1500)
    } catch (error) {
      console.error("[v0] Download error:", error)
      setStatus("error")
    } finally {
      setIsDownloading(false)
    }
  }

  const downloadAsZip = async () => {
    const { default: JSZip } = await import("jszip")
    const zip = new JSZip()

    for (let i = 0; i < posts.length; i++) {
      const post = posts[i]
      const fileUrl = post.sampleUrl || post.fileUrl

      if (fileUrl) {
        const fileExt = getFileExtensionFromUrl(fileUrl)
        setCurrentFile(`${post.id}.${fileExt}`)
        setDownloadedFiles(i + 1)
        setProgress(Math.round(((i + 1) / posts.length) * 100))

        try {
          const response = await fetch(fileUrl)
          const blob = await response.blob()
          const folderPath = createSubfolder ? subfolder : ""
          const fileName = `${post.id}.${fileExt}`
          zip.file(folderPath ? `${folderPath}/${fileName}` : fileName, blob)
        } catch (error) {
          console.error(`[v0] Failed to download ${post.id}:`, error)
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    // Generate and download ZIP
    const zipBlob = await zip.generateAsync({ type: "blob" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(zipBlob)
    link.download = `${sourceName || source}-${Date.now()}.zip`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  const downloadIndividual = async () => {
    for (let i = 0; i < posts.length; i++) {
      const post = posts[i]
      const fileUrl = post.sampleUrl || post.fileUrl

      if (fileUrl) {
        const fileExt = getFileExtensionFromUrl(fileUrl)
        setCurrentFile(`${post.id}.${fileExt}`)
        setDownloadedFiles(i + 1)
        setProgress(Math.round(((i + 1) / posts.length) * 100))

        try {
          const response = await fetch(fileUrl)
          const blob = await response.blob()
          const link = document.createElement("a")
          link.href = URL.createObjectURL(blob)
          link.download = `${post.id}.${fileExt}`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(link.href)
        } catch (error) {
          console.error(`[v0] Failed to download ${post.id}:`, error)
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 100))
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md bg-background/95 backdrop-blur-md border border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            {t("batchDownload")}
          </DialogTitle>
          <DialogDescription>
            {t("downloadLengthItemsFromSource", { length: posts.length, source })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Download Format Selection */}
          <div className="space-y-2">
            <Label>{t("downloadFormat")}</Label>
            <Select value={downloadFormat} onValueChange={(v: any) => setDownloadFormat(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">{t("individualFiles")}</SelectItem>
                <SelectItem value="zip">{t("zIPArchive")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Save Location */}
          <div className="space-y-2">
            <Label>{t("saveLocation")}</Label>
            <Select value={saveLocation} onValueChange={(v: any) => setSaveLocation(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="downloads">{t("downloads")}</SelectItem>
                <SelectItem value="documents">{t("documents")}</SelectItem>
                <SelectItem value="custom">{t("customPath")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Path Input */}
          {saveLocation === "custom" && (
            <div className="space-y-2">
              <Label htmlFor="custom-path">{t("customPath")}</Label>
              <Input
                id="custom-path"
                placeholder={t("pathToSave")}
                value={customSavePath}
                onChange={(e) => setCustomSavePath(e.target.value)}
              />
            </div>
          )}

          {/* Subfolder Option */}
          <div className="flex items-center justify-between">
            <Label htmlFor="subfolder" className="flex flex-col gap-1">
              <span>{t("createSubfolder")}</span>
              <span className="text-xs text-muted-foreground font-normal">{t("organizeFilesBySource", { source })}</span>
            </Label>
            <Switch id="subfolder" checked={createSubfolder} onCheckedChange={setCreateSubfolder} />
          </div>

          {/* Subfolder Name */}
          {createSubfolder && (
            <div className="space-y-2">
              <Label htmlFor="subfolder-name">{t("subfolderName")}</Label>
              <Input
                id="subfolder-name"
                placeholder={t("eGFavorites")}
                value={subfolder}
                onChange={(e) => setSubfolder(e.target.value)}
              />
            </div>
          )}

          {/* Progress Bar */}
          {isDownloading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>
                  {downloadedFiles} / {totalFiles}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div className="bg-accent h-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              {currentFile && <p className="text-xs text-muted-foreground truncate">{t("downloadingCurrentFile", { currentFile })}</p>}
            </div>
          )}

          {/* Status Messages */}
          {status === "complete" && (
            <div className="flex items-center gap-2 text-green-500 text-sm">
              <CheckCircle2 className="h-4 w-4" />
              {t("downloadCompletedSuccessfully")}
            </div>
          )}

          {status === "error" && (
            <div className="flex items-center gap-2 text-red-500 text-sm">
              <AlertCircle className="h-4 w-4" />
              {t("downloadFailedPleaseTryAgain")}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isDownloading}>
              {t("cancel")}
            </Button>
            <Button onClick={handleDownload} disabled={isDownloading || posts.length === 0} className="flex-1">
              {isDownloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("downloading")}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  {t("downloadCountFiles", { count: posts.length })}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
